import { FC, useState } from 'react';
import { Button } from '@ordr-as/umami-react/button';
import { TextInput } from '@ordr-as/umami-react/form';
import { Switch } from '@ordr-as/umami-react/switch';
import useIntl from '../../../../../hooks/useIntl';
import { useCreateDigitalBill, useQueryUnsettledDigitalBills } from '../../../../../hooks/useDigitalBillApi';
import { useLocation } from '../../../../../context/LocationContext';
import Spinner from '../../../../../components/Spinner';
import { FormattedMessage } from 'react-intl';
import ExistingTableWarning from './ExistingTableWarning';
import BackButton from '../../../../../components/BackButton';
import InvoiceInfoContainer from '../../../../../components/InvoiceInfoContainer';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useScreenSize } from '../../../../../context/ScreenSizeContext';
import CloseButton from '../../../../../components/CloseButton';
import { useDigitalBillActions } from '../../../../../context/DigitalBillStore';
import { useOpenBillPaymentMethods } from '../../../../../context/PaymentMethodStore';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

const CreateDigitalBillDialog = (props: Props) => {
  const { onClose, onCreated } = props;
  const intl = useIntl();
  const { isPhone } = useScreenSize();
  const { location } = useLocation();
  const { setActiveDigitalBill } = useDigitalBillActions();
  const { primaryReference } = useParams<{ primaryReference?: string }>();
  const predefinedTableNumber = primaryReference && parseInt(primaryReference) ? primaryReference : undefined;
  const [tableNumber, setTableNumber] = useState<string>(predefinedTableNumber || '');
  const [tableName, setTableName] = useState<string>();
  const [numberOfGuests, setNumberOfGuests] = useState<string>();
  const tableNumberWithoutLeading0s = tableNumber.replaceAll(/^0+/g, '');
  const { mutate, isPending } = useCreateDigitalBill(location.locationId);
  const [confirmUseExistingBill, setConfirmUseExistingBill] = useState<boolean>(false);

  const openBillPaymentMethods = useOpenBillPaymentMethods();
  const isInvoicePaymentMethodEnabled = openBillPaymentMethods.some(
    (paymentMethod) => paymentMethod.provider === 'UNINTEGRATED' && paymentMethod.paymentMethod === 'INVOICE'
  );
  const [isInvoiceCustomer, setIsInvoiceCustomer] = useState<boolean>(false);
  const [invoiceFieldValues, setInvoiceFieldValues] = useState<{
    customerRegistryId?: string;
    customerName?: string;
    referencePerson?: string;
    comment?: string;
  }>({});

  const tableNumberIsOnly0s = /^0*$/.test(tableNumber);
  const isInvalidTableNumber = !tableNumber || tableNumberIsOnly0s;

  const { data: unsettledBills, isSuccess } = useQueryUnsettledDigitalBills(location.locationId);

  if (!isSuccess) {
    return <></>;
  }

  const existingBill = unsettledBills.find(
    (bill) => bill.primaryReference === tableNumber || bill.primaryReference === tableNumberWithoutLeading0s
  );
  const tableExists = !!existingBill;

  const onInvoiceToggleChange = (isSelected: boolean) => {
    setInvoiceFieldValues({});
    setIsInvoiceCustomer(isSelected);
  };

  const handleCreateDigitalBill = () => {
    if (tableNumber && !isPending) {
      const sanitizedTableName = tableName?.trim().length ? tableName.trim() : undefined;
      const sanitizedNumberOfGuests =
        numberOfGuests && parseInt(numberOfGuests) >= 1 ? parseInt(numberOfGuests) : undefined;

      const invoiceData = {
        customerRegistryId: invoiceFieldValues.customerRegistryId,
        customerName: invoiceFieldValues.customerName,
        referencePerson: invoiceFieldValues.referencePerson,
        comment: invoiceFieldValues.comment,
      };

      mutate(
        {
          locationId: location.locationId,
          data: {
            deliveryPoint: { primaryReference: tableNumberWithoutLeading0s },
            paymentRule: 'PAY_LATER',
            tableName: sanitizedTableName,
            numberOfGuests: sanitizedNumberOfGuests,
            invoiceData: isInvoiceCustomer ? invoiceData : undefined,
          },
        },
        {
          onSuccess: (data) => {
            setActiveDigitalBill(data);
          },
          onSettled: () => {
            onCreated();
          },
        }
      );
    }
  };

  const handleAddToExistingBill = () => {
    const selectedDigitalBill = unsettledBills.find((bill) => bill.primaryReference === tableNumber)!;

    setActiveDigitalBill(selectedDigitalBill);

    onCreated();
  };

  const handleAction = () => {
    if (tableExists) {
      return handleAddToExistingBill();
    }

    handleCreateDigitalBill();
  };

  const shouldShowNoLeading0sHelpText = !tableExists && !tableNumberWithoutLeading0s && tableNumber.length > 0;

  const getButtonLabel = () => {
    if (tableExists) {
      return 'Select table';
    }

    return 'Open bill';
  };

  const invoiceSwitchClassName = classNames('flex justify-between items-center pb-3', {
    'border-b border-grey-lightest': isInvoiceCustomer,
  });

  const NavigationButton: FC = () => {
    const title = predefinedTableNumber ? `Table ${predefinedTableNumber}` : 'Create table';
    if (isPhone) return <BackButton title={title} navigateTo={onClose} />;
    return <CloseButton title={title} navigateTo={onClose} />;
  };

  return (
    <>
      <NavigationButton />
      <article className="flex flex-col h-full justify-between">
        <section className="flex p-4 flex-col gap-5">
          {!predefinedTableNumber && (
            <TextInput
              autoFocus
              value={tableNumber}
              onChange={setTableNumber}
              data-cy="table-number"
              label={intl.formatMessage({ id: `checkout.deliveryPoint.primary.TABLE` })}
              isDisabled={isPending}
              type="number"
              required
              fullWidth
              IconElement={isPending ? () => <Spinner className="self-center ml-1 w-5" /> : undefined}
            />
          )}

          {shouldShowNoLeading0sHelpText && (
            <p className="text-orange-darkest text-xs">
              <FormattedMessage id="digitalBill.create.leadingZerosDisallowed" />
            </p>
          )}
          {!!existingBill && (
            <ExistingTableWarning
              existingBill={existingBill}
              setConfirmUseExistingBill={setConfirmUseExistingBill}
              confirmUseExistingBill={confirmUseExistingBill}
            />
          )}
          <div className="flex gap-4">
            <TextInput
              value={tableName}
              onChange={setTableName}
              isDisabled={tableExists || isPending}
              label="Table name"
              data-cy="table-name"
              description="(Optional)"
              type="text"
              fullWidth
            />
            <TextInput
              value={numberOfGuests}
              onChange={setNumberOfGuests}
              isDisabled={tableExists || isPending}
              label="Number of guests"
              data-cy="number-of-guests"
              description="(Optional)"
              type="number"
              fullWidth
            />
          </div>
          {isInvoicePaymentMethodEnabled && (
            <>
              <div className={invoiceSwitchClassName}>
                <span>Invoice customer</span>
                <Switch
                  aria-label="Toggle for invoice customer"
                  className="m-0"
                  isSelected={isInvoiceCustomer}
                  onChange={onInvoiceToggleChange}
                  isDisabled={tableExists || isPending}
                />
              </div>
              {isInvoiceCustomer && !tableExists && (
                <div>
                  <InvoiceInfoContainer setInvoiceFieldValues={setInvoiceFieldValues} />
                </div>
              )}
            </>
          )}
        </section>
        <div className="flex sticky bottom-0 bg-brand-white p-4">
          <Button
            kind="primary"
            data-cy="create-bill"
            fullWidth
            isDisabled={
              isInvalidTableNumber ||
              (tableExists && !confirmUseExistingBill) ||
              (isInvoiceCustomer && !invoiceFieldValues.customerRegistryId && !invoiceFieldValues.customerName)
            }
            onClick={handleAction}
          >
            {getButtonLabel()}
          </Button>
        </div>
      </article>
    </>
  );
};

export default CreateDigitalBillDialog;
